/** @jsx jsx */

import { Fragment as _ } from 'react';
import { css, jsx } from '@emotion/core';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import CarouselSlidesAddRemoveWidget from './CarouselSlidesAddRemoveWidget';

// updated UI
import { H1 as H1_v2, H2 as H2_v2 } from '../EditorUtilities/UI/Headings';
import TogglePanel from '../EditorUtilities/UI/TogglePanel';
import CheckBox_v2 from '../EditorUtilities/UI/CheckBox';
import ColorPicker_v2 from '../EditorUtilities/UI/ColorPicker';
import Info from '../EditorUtilities/UI/Info';
import { addRemoveCarouselSlide } from './CarouselSlidesAddRemoveWidget';
import { ReorderInteractionWidget } from 'components/Editor/ReorderInteractionWidget/ReorderInteractionWidget';
import { colors } from 'styles/variables';
import NumberPicker_v2 from '../EditorUtilities/UI/NumberPicker';
//
import { getDefaultStyles } from 'components/lib/Carousel/CarouselConfig';

const { lGrey0, lGrey2, mint } = colors;
const togglePanelsCache = {};

// we want this to have the 3 states
// undefined
// 1. user has changed color
// 2. user has 'Got it'
let accessibilityMessageSeen: undefined | 1 | 2 = undefined;

// #1A1A2B and hover #6D7277
export const DEFAULT_PREV_NEXT_BTN_COLOR = '#1A1A2B';
// '#B2B6CA';
export const DEFAULT_PREV_NEXT_BTN_HOVER_COLOR = '#5F5F69';
// '#6D7277';
// '#D1D5E7';

const EditCarousel = ({ block }: { block?: any }) => {
  //
  const carouselDefaultStyles = getDefaultStyles(block?.meta?.packageVersion);

  //
  const { id: blockId } = block;
  const {
    carouselColor,
    border = true,
    includeText = true,
    borderColor = lGrey2,
    borderRadius = 10,
    includeSlideIndicators = true,
    carouselTextColor,
    backGround = carouselDefaultStyles?.backGround,
    //true,
    backgroundOpacity = 100,
    prevNextBtnColor = DEFAULT_PREV_NEXT_BTN_COLOR,
    prevNextBtnHoverColor = DEFAULT_PREV_NEXT_BTN_HOVER_COLOR,
    prevNextArrowColor = '#ffffff',
    animation = 'natural',
    lockDown,
    carouselHeight = 350,
    fullHeight = false,
  } = block.props;

  const carouselBlock = findByKeyVal_V2(blockId, 'type', 'Carousel');

  const saveTogglePanelState = (panel, open) => {
    togglePanelsCache[blockId] = {
      ...togglePanelsCache[blockId],
      [panel]: open,
    };

    setBlockProps(block, { ...block.props });
  };

  const prevNextBtnColorIsNotDefault =
    prevNextBtnColor !== DEFAULT_PREV_NEXT_BTN_COLOR ||
    prevNextBtnHoverColor !== DEFAULT_PREV_NEXT_BTN_HOVER_COLOR;

  return (
    <_>
      <TogglePanel
        label={<H1_v2>interaction functions & behaviours</H1_v2>}
        toggle={
          togglePanelsCache[blockId]
            ? togglePanelsCache[blockId]['interaction: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('interaction: settings', open)}
      >
        <CheckBox_v2
          label="Lock down"
          on={!!lockDown}
          onClick={() => setBlockProps(block, { lockDown: !!lockDown ? 0 : 1 })}
        />

        <div>
          <Info
            info="Click '+' to add a new slide. Click the trash icon of a slide you
            wish to remove."
          >
            Slides
          </Info>

          <div
            css={css({
              margin: '0 10px 0 0',
            })}
          >
            <ReorderInteractionWidget
              block={carouselBlock}
              addItem={() => {
                addRemoveCarouselSlide(carouselBlock);
              }}
              removeItem={index => {
                if (carouselBlock?.children?.length === 2) {
                  alert('At least two slides must exist.');
                  return;
                }

                if (
                  !window.confirm('Are you sure you want to remove this slide?')
                ) {
                  return;
                }

                addRemoveCarouselSlide(carouselBlock, index);
              }}
              getLabel={index => {
                return `Slide ${index + 1}`;
              }}
            />
          </div>
        </div>
      </TogglePanel>

      <TogglePanel
        label={<H1_v2>carousel styling</H1_v2>}
        toggle={
          togglePanelsCache[blockId]
            ? togglePanelsCache[blockId]['carousel: styling']
            : true
        }
        onToggle={open => saveTogglePanelState('carousel: styling', open)}
        hr={false}
      >
        <div style={{ margin: '10px 0 0 0' }}>
          <CheckBox_v2
            label="Include text"
            on={includeText}
            onClick={() => setBlockProps(block, { includeText: !includeText })}
          />
        </div>

        {includeText && (
          <ColorPicker_v2
            inColor={carouselTextColor}
            outColor={color =>
              setBlockProps(block, { carouselTextColor: color })
            }
            label="Text colour"
          />
        )}

        {accessibilityMessageSeen === 1 && prevNextBtnColorIsNotDefault && (
          <div
            className="fader_-5s"
            style={{
              padding: 10,
              fontSize: 12,
              background: mint,
              borderRadius: 5,
              color: '#fff',
              margin: '10px 10px 15px 0',
            }}
          >
            <div
              style={{
                margin: '0 0 10px 0',
              }}
            >
              When updating these colours take care to ensure they follow
              accessibility guidelines.
            </div>
            <div
              onClick={() => {
                accessibilityMessageSeen = 2;
                setBlockProps(block, { prevNextBtnColor });
              }}
              css={css({
                display: 'inline-flex',
                flexStretch: 0,
                justifyContent: 'center',
                alignItems: 'center',
                fontWeight: 700,
                cursor: 'pointer',
                background: '#fff',
                color: mint,
                padding: '5px 20px',
                borderRadius: 9999,
                ':hover': {
                  opacity: 0.8,
                },
              })}
            >
              Got it
            </div>
          </div>
        )}

        <ColorPicker_v2
          inColor={prevNextBtnColor}
          outColor={color => {
            // we've changed color so set accessibilityMessageSeen to 1 if undefined
            if (accessibilityMessageSeen === undefined) {
              accessibilityMessageSeen = 1;
            }

            console.log(color);

            setBlockProps(block, {
              prevNextBtnColor: color || DEFAULT_PREV_NEXT_BTN_COLOR,
            });
          }}
          label="Slide navigation button: background colour"
        />

        <ColorPicker_v2
          inColor={prevNextBtnHoverColor}
          outColor={color => {
            if (accessibilityMessageSeen === undefined) {
              accessibilityMessageSeen = 1;
            }

            setBlockProps(block, {
              prevNextBtnHoverColor: color || DEFAULT_PREV_NEXT_BTN_HOVER_COLOR,
            });
          }}
          label="Slide navigation button: hover colour"
        />

        <ColorPicker_v2
          inColor={prevNextArrowColor}
          outColor={color =>
            setBlockProps(block, { prevNextArrowColor: color })
          }
          label="Slide navigation button: arrow colour"
        />

        <CheckBox_v2
          label="Include background"
          on={backGround}
          onClick={() => setBlockProps(block, { backGround: !backGround })}
        />

        {backGround && (
          <_>
            <ColorPicker_v2
              inColor={carouselColor}
              outColor={color => {
                setBlockProps(block, { carouselColor: color });
              }}
              label="Background colour"
            />

            <div style={{ margin: '10px 0 0 0' }}>
              <NumberPicker_v2
                value={backgroundOpacity}
                limit={100}
                onChange={value => {
                  setBlockProps(block, { backgroundOpacity: value });
                }}
                label="Background opacity"
              />
            </div>

            <div style={{ margin: '10px 0 0 0' }}>
              <NumberPicker_v2
                value={borderRadius}
                onChange={value => {
                  setBlockProps(block, { borderRadius: value });
                }}
                label="Border radius"
              />
            </div>

            <div style={{ margin: '10px 0 0 0' }}>
              <CheckBox_v2
                label="Include border"
                on={border}
                onClick={() => setBlockProps(block, { border: !border })}
              />
            </div>

            {border && (
              <_>
                <div style={{ margin: '10px 0 0 0' }}>
                  <ColorPicker_v2
                    inColor={borderColor}
                    outColor={color => {
                      setBlockProps(block, { borderColor: color });
                    }}
                    label="Border colour"
                  />
                </div>
              </_>
            )}
          </_>
        )}

        <div
          style={{
            margin: '10px 0 0 0',
            pointerEvents: fullHeight ? 'none' : 'auto',
            opacity: fullHeight ? 0.2 : 1,
          }}
        >
          <NumberPicker_v2
            value={carouselHeight}
            onChange={value => {
              setBlockProps(block, { carouselHeight: value });
            }}
            label="Height"
          />
        </div>

        {/* <CheckBox_v2
          label="Full height"
          on={!!fullHeight}
          onClick={() =>
            setBlockProps(block, { fullHeight: !!fullHeight ? 0 : 1 })
          }
        /> */}

        <div style={{ margin: '10px 0 0 0' }}>
          <CheckBox_v2
            label="Include slide indicators"
            on={includeSlideIndicators}
            onClick={() =>
              setBlockProps(block, {
                includeSlideIndicators: !includeSlideIndicators,
              })
            }
          />
        </div>

        <div>
          <div
            css={css`
              font-size: 12px;
              color: #28292b;
              margin: 0 0 7px 0;
            `}
          >
            Slide animation style
          </div>
          <div
            css={css({
              '>div': {
                margin: '0 0 10px 0',
              },
            })}
          >
            <CheckBox_v2
              label="Natural"
              on={animation === 'natural'}
              onClick={() => setBlockProps(block, { animation: 'natural' })}
            />

            <CheckBox_v2
              label="Smooth"
              on={animation === 'smooth'}
              onClick={() => setBlockProps(block, { animation: 'smooth' })}
            />

            <CheckBox_v2
              label="None"
              on={animation === 'none'}
              onClick={() => setBlockProps(block, { animation: 'none' })}
            />
          </div>
        </div>
      </TogglePanel>
    </_>
  );
};

export default EditCarousel;
